import React, { useState, useEffect } from "react";

import { formatDistanceToNow, differenceInHours } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";

import {
  Delete,
  Autorenew,
  Storage,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Code as CodeIcon,
  Apps as AppsIcon,
  Info as InfoIcon,
  MonetizationOn,
  Settings as SettingsIcon,
  TrendingUp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";

import AppCard from "../components/appcard";
import Cost from "../components/cost";
import DeleteDialog from "../components/deleteDialog";
import Loader from "../components/loader";
import PredictedCost from "../components/predictedcost";
import useCluster from "../hooks/cluster";
import { useSnackbar } from "notistack";

export const SingleCluster = () => {
  const navigate = useNavigate();
  const { cluster } = useParams();
  const {
    loading,
    cluster: c,
    deleteCluster,
    refreshCluster,
    apps,
    kubeconfig,
  } = useCluster(cluster || "");
  const { enqueueSnackbar } = useSnackbar();
  const [showConf, setShowConf] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (showConf && !kubeconfig) {
      enqueueSnackbar("Unable to retrieve kubeconfig. Please try refreshing the cluster.", {
        variant: "warning"
      });
    }
  }, [showConf, kubeconfig, cluster, enqueueSnackbar]);

  if (loading) {
    return <Loader />;
  }

  if (!c || !c.name) {
    return <Loader text="Loading or cluster missing" />;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Open delete dialog
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  // Execute cluster deletion
  const confirmDeleteCluster = () => {
    deleteCluster(c.name).then(() => {
      // Navigate back to cluster list on success
      navigate("/clusters");
    });

    // Close dialog
    setDeleteDialogOpen(false);
  };

  // Handle dialog close
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Handle kubeconfig visibility toggle
  const toggleKubeconfig = () => {
    if (!showConf && !kubeconfig) {
      enqueueSnackbar("Unable to display kubeconfig. Please try again later.", {
        variant: "error"
      });
    }
    setShowConf(!showConf);
  };

  // Format cluster status
  const getStatusChip = () => {
    if (c?.micro?.stage === "done") {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Running"
          color="success"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
          }}
        />
      );
    } else if (c?.micro?.failures?.length) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Error"
          color="error"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<AccessTime />}
          label={`Creating: ${c?.micro?.stage || "initializing"}`}
          color="warning"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      );
    }
  };

  // Render failure messages if any
  const failuresSection = c?.micro?.failures?.length ? (
    <Alert
      severity="error"
      sx={{
        mt: 3,
        borderRadius: 2,
        "& .MuiAlert-icon": {
          alignItems: "flex-start",
          pt: 1.5,
        },
      }}
    >
      <AlertTitle>Cluster Failures</AlertTitle>
      <Stack spacing={1}>
        {c.micro.failures.map((fail, index) => (
          <Typography
            key={index}
            variant="body2"
            component="div"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {fail}
          </Typography>
        ))}
      </Stack>
    </Alert>
  ) : null;

  // Render apps section
  const appsSection = (
    <Box sx={{ mt: 3 }}>
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <AppsIcon color="primary" />
              <Typography variant="h6">Applications</Typography>
            </Stack>
          }
          subheader={`${apps?.length || 0} application${apps?.length !== 1 ? "s" : ""} deployed`}
        />
        <Divider />
        <CardContent>
          {!apps?.length ? (
            <Alert severity="info">
              No applications have been deployed to this cluster yet.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {apps.map((app) => (
                <Grid item xs={12} sm={6} md={4} key={app.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      height: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <AppCard app={app} c={c} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Render kubeconfig section
  const kubeconfigSection = (
    <Box sx={{ mt: 3 }}>
      <Card sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <CodeIcon color="primary" />
              <Typography variant="h6">Kubernetes Configuration</Typography>
            </Stack>
          }
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={showConf}
                  onChange={toggleKubeconfig}
                  color="primary"
                />
              }
              label="Show KubeConfig"
              sx={{ mr: 1 }}
            />
          }
        />
        <Divider />
        <CardContent>
          {showConf ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {kubeconfig ? (
                <Box
                  component="pre"
                  sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                >
                  {kubeconfig}
                </Box>
              ) : (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <AlertTitle>Failed to load kubeconfig</AlertTitle>
                  <Typography variant="body2">
                    Unable to retrieve kubeconfig data. Please try refreshing the cluster.
                  </Typography>
                </Alert>
              )}
            </Paper>
          ) : (
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2 }}>
              Toggle the switch to view your Kubernetes configuration for
              connecting to this cluster.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Main cluster controls
  const clusterControls = (
    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
      <Button
        variant="outlined"
        startIcon={<Autorenew />}
        onClick={() => {
          refreshCluster(c.name).catch((err) => {
            enqueueSnackbar(`Failed to refresh cluster ${c.name}: ${err.message}`, {
              variant: "error"
            });
          });
        }}
        sx={{ borderRadius: 2 }}
      >
        Refresh Cluster
      </Button>

      <Button
        variant="outlined"
        color="error"
        startIcon={<Delete />}
        onClick={openDeleteDialog}
        sx={{
          borderRadius: 2,
          borderColor: "#d32f2f",
          color: "#d32f2f",
          "&:hover": {
            borderColor: "#b71c1c",
            backgroundColor: "rgba(211, 47, 47, 0.04)",
          },
        }}
      >
        Delete Cluster
      </Button>
    </Stack>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Delete dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteCluster}
        title="Delete Cluster"
        itemName={c.name}
      />

      {/* Cluster Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(to bottom, rgba(26, 30, 35, 0.8), rgba(26, 30, 35, 1))",
          mb: 4,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "start", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Storage fontSize="large" sx={{ color: "primary.main" }} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    background:
                      "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {c.name}
                </Typography>
              </Stack>
              {getStatusChip()}
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Created {formatDistanceToNow(new Date(c.created))} ago
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* Current cost */}
            <Tooltip title="Current cost based on actual usage">
              <Card
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: "rgba(67, 211, 255, 0.1)",
                  border: "1px solid",
                  borderColor: "rgba(67, 211, 255, 0.2)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <MonetizationOn color="primary" />
                  <Cost variant="h6" pennies={c.micro.usage} />
                </Stack>
              </Card>
            </Tooltip>

            {/* Predicted cost */}
            <Tooltip title="Predicted monthly cost based on current usage">
              <Card
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: "rgba(255, 167, 38, 0.1)",
                  border: "1px solid",
                  borderColor: "rgba(255, 167, 38, 0.2)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <TrendingUp sx={{ color: "warning.main" }} />
                  <PredictedCost
                    variant="h6"
                    pennies={c.micro.usage}
                    hoursActive={
                      differenceInHours(new Date(), new Date(c.created)) || 1
                    }
                  />
                </Stack>
              </Card>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Show navigation tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="cluster management tabs"
            sx={{
              "& .MuiTab-root": {
                minWidth: "auto",
                px: 3,
                py: 1.5,
                borderRadius: "8px 8px 0 0",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
            }}
          >
            <Tab
              icon={<AppsIcon />}
              iconPosition="start"
              label="Applications"
            />
            <Tab
              icon={<CodeIcon />}
              iconPosition="start"
              label="Configuration"
            />
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Settings"
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Failure messages */}
      {failuresSection}

      {/* Tab content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && appsSection}
        {activeTab === 1 && kubeconfigSection}
        {activeTab === 2 && (
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={1} alignItems="center">
                  <SettingsIcon color="primary" />
                  <Typography variant="h6">Cluster Settings</Typography>
                </Stack>
              }
            />
            <Divider />
            <CardContent>{clusterControls}</CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default SingleCluster;
